import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { LanguageCode } from 'src/app/_helpers/nationalities';
import { ProposalService } from 'src/app/_services/proposal.service';
import { UserAuthService } from 'src/app/_services/user-auth.service';
import { ProposalDetails } from 'src/app/_types/proposals.type';
import { IconsModule } from 'src/app/shared/icons/icons.module';

import { ActionModalComponent } from '../../action-modal/action-modal.component';
import { AddProposalModalComponent } from '../../add-proposal-modal/add-proposal-modal.component';
import { PdfViewerModalComponent } from '../../pdf-viewer-modal/pdf-viewer-modal.component';
import { ProposalDisplayerComponent } from '../proposal-displayer/proposal-displayer.component';
import { syncProposalSegmentHeights } from '../proposal-segment-heights';

@Component({
  selector: 'app-proposals-actions',
  templateUrl: './proposals-actions.component.html',
  styleUrls: ['./proposals-actions.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ActionModalComponent,
    PdfViewerModalComponent,
    AddProposalModalComponent,
    ProposalDisplayerComponent,
    FormsModule,
    IconsModule,
  ],
})
export class ProposalsActionsComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() leadId: string;

  @Input() proposals: ProposalDetails[] = [];

  @Input() lngCode: LanguageCode = 'fr';

  @Input() showViewIcon = true;

  @Input() singleRowScrollable = false;

  @Output() editProposal = new EventEmitter<ProposalDetails>();

  @Output() proposalsChanged = new EventEmitter<void>();

  @ViewChild('deletionModal')
  deletionModal!: ActionModalComponent;

  @ViewChild('sendModal')
  sendModal!: ActionModalComponent;

  @ViewChild('pdfViewer')
  viewer!: PdfViewerModalComponent;

  private selectedProposalId: string | null = null;

  private proposalHeightFrame?: number;

  private viewInitialized = false;

  submitted = false;

  withoutNotification = false;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly userAuthService: UserAuthService,
    private readonly proposalService: ProposalService,
    private readonly toastrService: ToastrService
  ) {}

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.scheduleProposalHeightSync();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.viewInitialized && changes['proposals']) {
      this.scheduleProposalHeightSync();
    }
  }

  ngOnDestroy(): void {
    if (this.proposalHeightFrame) {
      cancelAnimationFrame(this.proposalHeightFrame);
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.viewInitialized) {
      this.scheduleProposalHeightSync();
    }
  }

  private scheduleProposalHeightSync(): void {
    if (this.proposalHeightFrame) {
      cancelAnimationFrame(this.proposalHeightFrame);
    }
    this.proposalHeightFrame = requestAnimationFrame(() =>
      syncProposalSegmentHeights(this.elementRef.nativeElement)
    );
  }

  trackByProposalId(_: number, proposal: ProposalDetails): string {
    return proposal.proposalId;
  }

  onDeleteProposal(proposalId?: string): void {
    if (!proposalId) {
      return;
    }
    this.selectedProposalId = proposalId;
    this.deletionModal.openModal();
  }

  onSendProposal(proposalId?: string): void {
    if (!proposalId) {
      return;
    }
    this.selectedProposalId = proposalId;
    this.withoutNotification = false;
    this.sendModal.openModal();
  }

  submitDeleteProposal(): void {
    if (this.submitted || !this.selectedProposalId) {
      return;
    }
    this.submitted = true;
    this.proposalService
      ._deleteSavedLeadProposal([this.selectedProposalId])
      .subscribe({
        next: () => {
          this.toastrService.success($localize`Offre supprimée avec succès`);
          this.closeDeleteModal();
          this.proposalsChanged.emit();
          this.submitted = false;
        },
        error: () => {
          this.toastrService.error(
            $localize`Une erreur est survenue lors de la suppression de l'offre`
          );
          this.submitted = false;
        },
      });
  }

  submitSendProposal(): void {
    if (this.submitted || !this.selectedProposalId) {
      return;
    }
    this.submitted = true;
    const sendPayload = {
      proposalIds: [this.selectedProposalId],
      ...(this.withoutNotification ? { withoutNotification: true } : {}),
    };
    this.proposalService._sendProposal(sendPayload).subscribe({
      next: () => {
        this.toastrService.success($localize`Offre envoyée avec succès`);
        this.closeSendModal();
        this.proposalsChanged.emit();
        this.withoutNotification = false;
        this.submitted = false;
      },
      error: (err: Error) => {
        const errRes: any = err;
        this.toastrService.error(
          errRes?.error?.error?.clientErrorMessage ??
            $localize`Une erreur est survenue lors de l'envoi de l'offre`
        );
        this.withoutNotification = false;
        this.submitted = false;
      },
    });
  }

  viewFile(key: string): void {
    if (key.includes('https://')) {
      this.setFileUrl(key);
      return;
    }
    this.userAuthService.getPresignedUrl(key).subscribe((res) => {
      if (res.status === 'SUCCESS') {
        this.setFileUrl(res.data.preSignedUrl);
      }
    });
  }

  private setFileUrl(url: string): void {
    this.viewer.pdfFileUrl = url;
    this.viewer.openModal();
  }

  closeDeleteModal(): void {
    this.deletionModal?.closeModal();
  }

  closeSendModal(): void {
    this.withoutNotification = false;
    this.sendModal?.closeModal();
  }
}
